// Messenger API — типизированные обёртки над backend /api/v1/messenger.
// Контракт сверен с apps/api messenger.controller + messenger.service.

import { api } from '@/lib/api';
import { branchQueryString, setBranchParams } from '@/lib/branch-params';

export interface ConversationListItem {
  /** Диалог привязан к сотруднику: его id и имя (null — не привязан). */
  employeeId?: string | null;
  employeeName?: string | null;
  id: string;
  platform: string;
  clientId: string | null;
  clientName: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactUsername: string | null;
  /** 'open' — рабочий список, 'closed' — архив (М3 батча №20). */
  status: string;
  lastMessageAt: string | null;
  lastInboundAt: string | null;
  lastMessagePreview: string | null;
  unreadCount: number;
  /**
   * Диалог сотрудника (М4 батча №20): онбординг завершился состоянием
   * 'employee' ЛИБО телефон контакта совпал с телефоном действующего
   * сотрудника. Опционально для совместимости со старым кэшем/моками.
   */
  isEmployee?: boolean;
  /**
   * Групповой чат (батч №23 П10): группа, супергруппа, канал. Читаем, но не
   * пишем — поле ввода в таком диалоге отключено, сервер тоже откажет.
   * Опционально по той же причине, что isEmployee: старый кэш и фикстуры.
   */
  isGroup?: boolean;
  /**
   * Плашки списка (план 62 П3): «Новый» — привязанный клиент без оплаченной
   * услуги, его ведёт менеджер (имя — из открытой сделки); «Действующий» —
   * оплата была. Опционально по той же причине, что isEmployee: старый кэш и
   * литеральные фикстуры тестов.
   */
  isNewClient?: boolean;
  hasPaidService?: boolean;
  managerName?: string | null;
  /** Фото для кружка списка (план 62 П4). */
  clientPhotoUrl?: string | null;
  employeePhotoUrl?: string | null;
  /**
   * Плашка «Представитель» (правка владельца 19.08): статус либо тип карточки,
   * либо фактические подопечные. Опционально — как остальные плашки: старый кэш
   * и литеральные фикстуры тестов.
   */
  isRepresentative?: boolean;
  /** Подопечные — кнопки «Ребёнок: Имя» в шапке диалога. */
  wards?: Array<{ id: string; name: string }>;
  /**
   * Представители — кнопки «Представитель: Имя» в шапке (план 104 Б10). В
   * детском чате пишет родитель: оператору нужна его карточка и запись его
   * самого на занятие. `clientType` гасит «Записать» у того, кто только
   * представитель и сам не занимается.
   */
  representatives?: Array<{ id: string; name: string; clientType: string }>;
  /**
   * Локация карточки собеседника (правка владельца 19.08). В списке и в поиске
   * по нему тёзки различаются только филиалом.
   */
  branchName?: string | null;
  /**
   * Филиал КОНТЕКСТА ДИАЛОГА для шаблонов сообщений (план 103): локация
   * клиента → филиал канала → null. Композер обязан фильтровать шаблоны по
   * нему, а не по выбору в шапке: сотрудник Мурманска при «Все филиалы» видел
   * шаблоны Бутово.
   */
  templateBranchId?: string | null;
  /** Дата рождения собеседника — шапка чата показывает возраст (план 103). */
  clientBirthDate?: string | null;
  /**
   * Телефон КАРТОЧКИ собеседника (план 105 П3). Шапка показывает его, когда у
   * диалога своего номера нет: в Telegram и ВКонтакте контакт номером не
   * делится, а карточка его знает.
   */
  clientPhone?: string | null;
  /**
   * Имя и фамилия контакта порознь (план 105 П4) — префилл формы «Новый
   * клиент» из диалога. Склеенный `contactName` для этого не годится: он
   * собран как «Имя Фамилия», а разбор строки поиска читает «Фамилия Имя».
   */
  contactFirstName?: string | null;
  contactLastName?: string | null;
  /** Сегмент клиента — плашка в шапке чата (план 103). */
  clientStatus?: string | null;
  /** Явная локация диалога (план 103): пометка человека, NULL — по клиенту/каналу. */
  branchId?: string | null;
  /**
   * Совпадение по ТЕКСТУ сообщений (план 135): id самого свежего совпавшего
   * сообщения диалога и фрагмент вокруг совпадения (±60 символов с «…»).
   *
   * null — диалог попал в выдачу по имени, телефону или @username, а не по
   * тексту, либо поиска не было вовсе: строка списка выглядит как обычно.
   * Опционально по той же причине, что isEmployee: старый кэш и литеральные
   * фикстуры тестов, где эти поля не заданы.
   */
  matchMessageId?: string | null;
  matchSnippet?: string | null;
}

export type MessageDirection = 'inbound' | 'outbound';

/** Цитата: сообщение, на которое отвечают (М2 батча №20). */
export interface ReplyPreview {
  id: string;
  direction: MessageDirection;
  body: string;
  senderName: string | null;
}

export interface ThreadMessage {
  id: string;
  direction: MessageDirection;
  body: string;
  attachments: unknown[];
  senderUserId: string | null;
  senderName: string | null;
  /**
   * Кто написал входящее в групповом чате (имя из мессенджера). Опционально
   * по той же причине, что isEmployee выше: старый кэш и фикстуры тестов.
   */
  authorName?: string | null;
  /** Нашёлся ли автор в базе — только при ЕДИНСТВЕННОМ совпадении по ФИО. */
  authorKind?: 'client' | 'employee' | null;
  deliveryStatus: string;
  error: string | null;
  createdAt: string;
  /** Исходящее прочитано клиентом в мини-аппе (Ф4 батч №17). */
  read: boolean;
  /** Отозвано у клиента (docs/service/56): ISO-время либо null. */
  deletedAt: string | null;
  deletionStatus: 'pending' | 'done' | 'failed' | 'local_only' | null;
  deletedByName: string | null;
  /** Момент фактической отправки — по нему считаем, не истекло ли окно отзыва. */
  sentAt: string | null;
  /** На какое сообщение это ответ (М2 батча №20). null — обычное. */
  replyTo?: ReplyPreview | null;
}

/** Сообщение в объединённой ленте «Все мессенджеры» — с платформой своего диалога. */
export interface ClientThreadMessage extends ThreadMessage {
  platform: string;
  /**
   * Диалог сообщения (план 152 C5): цитата допустима только у сообщений того
   * диалога, куда уйдёт ответ, а платформы у двух диалогов клиента совпадают.
   */
  conversationId: string;
}

/** Диалог, куда уйдёт ответ по клиенту, — id и канал. */
export interface ReplyConversation {
  id: string;
  platform: string;
}

/**
 * Лента «Все каналы» и канал ответа ПО УМОЛЧАНИЮ (план 152 C5). Канал считает
 * сервер той же функцией, что выбирает диалог при отправке, — админка его не
 * вычисляет: подпись и фактическая отправка расходиться не могут.
 */
export interface ClientFeed {
  items: ClientThreadMessage[];
  /** null — у клиента нет диалогов, отвечать некуда. */
  replyConversation: ReplyConversation | null;
}

export interface MessengerSummary {
  unreadConversations: number;
  unreadMessages: number;
}

export interface ListConversationsParams {
  status?: 'open' | 'closed' | 'all' | undefined;
  scope?: 'all' | 'unlinked' | 'linked' | 'employees' | 'groups' | undefined;
  clientId?: string | undefined;
  /**
   * Каналы СОТРУДНИКА (план 105 П2). Склейка по клиенту и телефону на
   * сотрудниках рвётся: часть диалогов привязана к карточке, часть нет, а у
   * ВКонтакте телефона нет вовсе.
   */
  employeeId?: string | undefined;
  /**
   * Филиал из шапки (план 80): транзитом через филиал клиента диалога. С
   * `clientId` сервер его игнорирует — вкладка «Чат» в карточке видна всегда.
   */
  /** Филиалы шапки (план 106, мультивыбор); пусто — все. */
  branchIds?: string[] | undefined;
  q?: string | undefined;
  /** Только непрочитанные. Считает сервер: страница видит не весь список. */
  unreadOnly?: boolean | undefined;
  limit?: number | undefined;
}

export async function listConversations(
  params: ListConversationsParams,
): Promise<ConversationListItem[]> {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.scope) qs.set('scope', params.scope);
  if (params.clientId) qs.set('client_id', params.clientId);
  if (params.employeeId) qs.set('employee_id', params.employeeId);
  setBranchParams(qs, params.branchIds);
  if (params.q) qs.set('q', params.q);
  if (params.unreadOnly) qs.set('unread_only', 'true');
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  const res = await api.get<{ items: ConversationListItem[] }>(
    `/messenger/conversations${query ? `?${query}` : ''}`,
  );
  return res.items;
}

/**
 * Заголовок одного диалога. Нужен, когда в адресе стоит `?conversation=`, а в
 * списке этого диалога нет: список отфильтрован филиалом из шапки, и переход
 * по ссылке из другого филиала иначе молча ни к чему не приводил бы.
 */
export async function getConversation(conversationId: string): Promise<ConversationListItem> {
  return api.get<ConversationListItem>(`/messenger/conversations/${conversationId}`);
}

export async function getMessages(conversationId: string): Promise<ThreadMessage[]> {
  const res = await api.get<{ items: ThreadMessage[] }>(
    `/messenger/conversations/${conversationId}/messages`,
  );
  return res.items;
}

export async function sendReply(
  conversationId: string,
  text: string,
  replyToMessageId?: string | null,
  mediaId?: string | null,
): Promise<ThreadMessage> {
  const res = await api.post<{ message: ThreadMessage }>(
    `/messenger/conversations/${conversationId}/messages`,
    {
      text,
      ...(replyToMessageId ? { reply_to_message_id: replyToMessageId } : {}),
      ...(mediaId ? { media_id: mediaId } : {}),
    },
  );
  return res.message;
}

/**
 * Привязать диалог к СОТРУДНИКУ (правка владельца 2026-07-30): сотрудники тоже
 * пишут в мессенджеры школы, а раньше их можно было только опознать по телефону.
 */
export function linkConversationToEmployee(
  conversationId: string,
  employeeId: string,
): Promise<ConversationListItem> {
  return api.post<ConversationListItem>(
    `/messenger/conversations/${conversationId}/link-employee`,
    {
      employee_id: employeeId,
    },
  );
}

/**
 * Кандидат в каналы сотрудника (план 107): диалог, похожий на его переписку и
 * ещё ни к кому не привязанный.
 *
 * `reason` — чем похож, `strong` — насколько сигналу можно верить без человека.
 * Сильный (тот же аккаунт мессенджера) форма предотмечает, средний (совпал
 * телефон) — нет: семейный номер держит вся семья.
 */
export interface EmployeeChannelCandidate {
  id: string;
  platform: string;
  contactName: string | null;
  contactPhone: string | null;
  contactUsername: string | null;
  clientId: string | null;
  clientName: string | null;
  status: 'open' | 'closed';
  lastMessageAt: string | null;
  reason: 'external_id' | 'username' | 'phone' | 'client_phone';
  strong: boolean;
}

export async function listEmployeeLinkCandidates(
  employeeId: string,
): Promise<EmployeeChannelCandidate[]> {
  const res = await api.get<{ items: EmployeeChannelCandidate[] }>(
    `/messenger/employees/${employeeId}/link-candidates`,
  );
  return res.items;
}

/** Привязать к сотруднику сразу несколько диалогов (план 107). */
export function linkEmployeeConversations(
  employeeId: string,
  conversationIds: string[],
): Promise<{ linked: string[]; skipped: string[] }> {
  return api.post<{ linked: string[]; skipped: string[] }>(
    `/messenger/employees/${employeeId}/link-conversations`,
    { conversation_ids: conversationIds },
  );
}

/** Вложение, загруженное для сообщения в чате (docs/service/56). */
export interface ChatMedia {
  media_id: string;
  kind: 'photo' | 'video' | 'animation' | 'document';
  mime: string;
  filename: string | null;
}

/**
 * Загрузить файл для отправки в переписку. `purpose=chat` — право
 * `messenger:reply`, а не «Массовая рассылка»: приложить PDF в чат должен
 * уметь и администратор без права рассылки.
 */
export function uploadChatMedia(file: File): Promise<ChatMedia> {
  const form = new FormData();
  form.append('file', file);
  return api.post<ChatMedia>('/messenger/media?purpose=chat', form);
}

/** Что произошло при отзыве — фронт показывает это на пузыре. */
export type DeletionOutcome = 'cancelled' | 'queued' | 'local_only';

/**
 * Отозвать отправленное сообщение у клиента (docs/service/56).
 * Строка в истории остаётся с плашкой «отозвано» — отзыв не прячет ошибку от
 * коллег, он убирает сообщение у клиента.
 */
export async function deleteSentMessage(messageId: string): Promise<DeletionOutcome> {
  const res = await api.delete<{ outcome: DeletionOutcome }>(`/messenger/messages/${messageId}`);
  return res.outcome;
}

// «Все мессенджеры»: объединённая лента по клиенту + канал ответа по умолчанию.
export async function getClientFeed(clientId: string): Promise<ClientFeed> {
  const res = await api.get<{
    items: ClientThreadMessage[];
    reply_conversation?: ReplyConversation | null;
  }>(`/messenger/clients/${clientId}/messages`);
  return { items: res.items, replyConversation: res.reply_conversation ?? null };
}

/**
 * Ответ по клиенту. `conversationId` — диалог, выбранный в «Ответ: канал ▾»
 * (план 152 C5); сервер проверит, что он этого клиента. Без него сервер
 * выберет канал по умолчанию сам.
 */
export async function sendClientReply(
  clientId: string,
  text: string,
  replyToMessageId?: string | null,
  mediaId?: string | null,
  conversationId?: string | null,
): Promise<ClientThreadMessage> {
  const res = await api.post<{ message: ClientThreadMessage }>(
    `/messenger/clients/${clientId}/messages`,
    {
      text,
      ...(replyToMessageId ? { reply_to_message_id: replyToMessageId } : {}),
      ...(mediaId ? { media_id: mediaId } : {}),
      ...(conversationId ? { conversation_id: conversationId } : {}),
    },
  );
  return res.message;
}

export async function linkConversation(
  conversationId: string,
  clientId: string,
  /**
   * «Номер чужой, но это тот человек» (разбор 27.08). Без флага сервер
   * отвечает 409 `messenger.link_phone_mismatch`, если номер диалога не значится
   * ни в одном телефоне карточки.
   */
  confirmPhoneMismatch = false,
): Promise<ConversationListItem> {
  return api.post<ConversationListItem>(`/messenger/conversations/${conversationId}/link`, {
    client_id: clientId,
    ...(confirmPhoneMismatch ? { confirm_phone_mismatch: true } : {}),
  });
}

/**
 * Отвязать. `what` обязателен по смыслу: связи с контактом и сотрудником
 * независимы, у человека, который и то и другое, стоят обе.
 */
export async function unlinkConversation(
  conversationId: string,
  what: 'client' | 'employee' | 'both' = 'client',
): Promise<ConversationListItem> {
  return api.post<ConversationListItem>(
    `/messenger/conversations/${conversationId}/unlink?what=${what}`,
  );
}

// Архив диалога (М3 батча №20). Архив ≠ удаление: диалог уходит из рабочего
// списка, остаётся в поиске и возвращается сам при новом входящем.
export async function archiveConversation(conversationId: string): Promise<ConversationListItem> {
  return api.post<ConversationListItem>(`/messenger/conversations/${conversationId}/archive`);
}

export async function unarchiveConversation(conversationId: string): Promise<ConversationListItem> {
  return api.post<ConversationListItem>(`/messenger/conversations/${conversationId}/unarchive`);
}

/**
 * Пометить диалог групповым (или снять пометку) вручную.
 *
 * Тип чата площадка сообщает только в самом входящем, поэтому рабочий чат,
 * который молчит неделями, автоматика не разметит. Сотрудник, который его
 * ведёт, знает про него больше любой эвристики. Снятие нужно на случай
 * ошибки: в помеченный диалог CRM не пишет вовсе.
 */
export async function markConversationGroup(
  conversationId: string,
  isGroup: boolean,
): Promise<ConversationListItem> {
  const action = isGroup ? 'group' : 'ungroup';
  return api.post<ConversationListItem>(`/messenger/conversations/${conversationId}/${action}`);
}

/**
 * Локация диалога вручную (план 103): «групповые чаты и сотрудников привязать
 * к локации». null — снять пометку (снова считается по клиенту/каналу).
 */
export async function setConversationBranch(
  conversationId: string,
  branchId: string | null,
): Promise<ConversationListItem> {
  return api.post<ConversationListItem>(`/messenger/conversations/${conversationId}/branch`, {
    branch_id: branchId,
  });
}

export async function markRead(conversationId: string): Promise<void> {
  await api.post<void>(`/messenger/conversations/${conversationId}/read`);
}

export async function markUnread(conversationId: string): Promise<void> {
  await api.post<void>(`/messenger/conversations/${conversationId}/unread`);
}

export async function getSummary(branchIds?: string[]): Promise<MessengerSummary> {
  // Филиалы из шапки — те же, что уходят в список диалогов. Иначе бейдж
  // считает одно, а раздел показывает другое.
  return api.get<MessengerSummary>(`/messenger/summary${branchQueryString(branchIds)}`);
}

// Лёгкий поиск клиентов для привязки диалога (переиспользуем список клиентов).
export interface ClientSearchItem {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  /**
   * Локация карточки (правка владельца 19.08): у тёзок ФИО и телефон совпадают,
   * а филиал отличается — по нему и понятно, кого привязывать. Название приходит
   * с сервера: окно поиска всплывающее, второй запрос за справочником там лишний.
   */
  branchName: string | null;
}

export async function searchClientsForLink(q: string): Promise<ClientSearchItem[]> {
  const res = await api.get<{
    items: Array<{
      id: string;
      firstName: string;
      lastName: string;
      phone: string | null;
      primaryBranchName: string | null;
    }>;
    // Объединённые карточки не предлагаем (план 70): привязать диалог к
    // архивной — то же самое, что записать на неё занятие.
    // Архивные тоже не предлагаем (правка владельца 19.08): диалог, привязанный
    // к архивной карточке, нельзя ни записать на занятие, ни продать абонемент —
    // менеджер узнаёт об этом уже после привязки.
  }>(`/clients?q=${encodeURIComponent(q)}&limit=10&exclude_merged=true&exclude_archived=true`);
  return res.items.map((c) => ({
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    phone: c.phone,
    branchName: c.primaryBranchName ?? null,
  }));
}

// Поиск сотрудника для привязки диалога (правка владельца 2026-07-31).
//
// Отдельно от справочника `/employees/lookup`: тот без ПД и не умеет искать по
// телефону, а менеджер копирует номер прямо из шапки диалога. Сервер сверяет
// телефон по цифрам и наружу его не отдаёт.
export interface EmployeeSearchItem {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
}

export async function searchEmployeesForLink(q: string): Promise<EmployeeSearchItem[]> {
  const res = await api.get<{ items: EmployeeSearchItem[] }>(
    `/messenger/employees?q=${encodeURIComponent(q)}`,
  );
  return res.items;
}

/**
 * «Поделиться» занятиями/группами из чата (план 132 §2.2): хотя бы одно поле
 * непустое, суммарно не больше 10 занятий — иначе сервер отвечает 422
 * (`messenger.share_nothing` / `messenger.share_too_many`).
 */
export interface ShareScheduleBody {
  occurrence_ids?: string[];
  group_ids?: string[];
  /** Фильтр «Филиал» модалки: занятия групп берутся только в нём. */
  branch_ids?: string[];
}

export interface ShareScheduleResult {
  sent: number;
  message_ids: string[];
}

/** Цель отправки — та же развилка, что у обычного ответа (по диалогу или по клиенту). */
export type ShareScheduleTarget = { conversationId: string } | { clientId: string };

export async function shareSchedule(
  target: ShareScheduleTarget,
  body: ShareScheduleBody,
): Promise<ShareScheduleResult> {
  const path =
    'conversationId' in target
      ? `/messenger/conversations/${target.conversationId}/share-schedule`
      : `/messenger/clients/${target.clientId}/share-schedule`;
  return api.post<ShareScheduleResult>(path, body);
}

/** Каналы, куда CRM умеет написать первой (план 92). */
export type WriteFirstPlatform = 'wazzup_tg' | 'wazzup_wa' | 'wazzup_max';

export interface WriteFirstResponse {
  conversation_id: string;
  sent_to_group: boolean;
  platform: WriteFirstPlatform;
  /** Чем написали: по юзернейму или по номеру (для Telegram это важно). */
  address_kind: 'username' | 'phone';
}

/**
 * Написать клиенту ПЕРВЫМ через Wazzup — по адресу из карточки
 * (docs/service/91, каналы — 92). Адрес в теле не передаём: сервер берёт его
 * сам, иначе его можно было бы подменить из браузера.
 *
 * Ключ идемпотентности задаём явно: он уходит в Wazzup как `crmMessageId` и
 * в их окне 60 секунд гасит повтор после сетевого таймаута.
 */
export async function writeFirst(
  clientId: string,
  text: string,
  idempotencyKey: string,
  /**
   * Канал (план 92). Четвёртым аргументом намеренно: тесты плана 91 читают
   * ключ идемпотентности позиционно, третьим. Не передан — Telegram.
   */
  platform?: WriteFirstPlatform,
): Promise<WriteFirstResponse> {
  return api.post<WriteFirstResponse>(
    `/messenger/clients/${clientId}/write-first`,
    { text, ...(platform ? { platform } : {}) },
    { idempotencyKey },
  );
}
